/**
 * 🏷️ Nombres legibles de roles (espejo de `RoleType.getLabel()` en el backend).
 *
 * Úsalo cuando solo tienes el código del rol (string). Cuando el dato viene
 * del endpoint de roles, preferí `role.label` que ya llega resuelto.
 */
export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrador',
  SUPPORT: 'Soporte técnico',
  USER: 'Usuario',
  SELLER: 'Vendedor',
  CASHIER: 'Cajero',
  WAREHOUSE: 'Bodega',
  ACCOUNTANT: 'Contador',
  RESTAURANT_MANAGER: 'Gerente de restaurante',
  HOST: 'Anfitrión',
  WAITER: 'Mesero',
  CHEF: 'Chef',
};

const prettify = (code: string) =>
  code
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/**
 * Devuelve el nombre legible del rol. Acepta tanto el código de BD (`SUPPORT`)
 * como el que viene en el JWT con prefijo Spring (`ROLE_SUPPORT`).
 * Si no lo conoce, formatea el código.
 */
export const roleLabel = (code?: string | null): string => {
  if (!code) return '';
  const normalized = code.startsWith('ROLE_') ? code.slice(5) : code;
  return ROLE_LABELS[normalized] ?? ROLE_LABELS[code] ?? prettify(normalized);
};

/**
 * Orden de relevancia para elegir "el rol" de un usuario que tiene varios.
 * Administración primero, luego los roles de restaurante, luego comercio.
 */
const ROLE_PRIORITY = [
  'ADMIN',
  'SUPPORT',
  'ACCOUNTANT',
  'RESTAURANT_MANAGER',
  'CHEF',
  'HOST',
  'WAITER',
  'CASHIER',
  'SELLER',
  'WAREHOUSE',
  'USER',
];

const stripPrefix = (code: string) =>
  code.startsWith('ROLE_') ? code.slice(5) : code;

/**
 * Del conjunto de roles del usuario (códigos de BD o con prefijo `ROLE_`),
 * devuelve el nombre legible del más relevante. `fallback` cuando no hay roles.
 */
export const primaryRoleLabel = (
  roles?: readonly string[] | null,
  fallback = 'Sin rol'
): string => {
  if (!roles || roles.length === 0) return fallback;
  const present = new Set(roles.map(stripPrefix));
  const picked = ROLE_PRIORITY.find((r) => present.has(r)) ?? stripPrefix(roles[0]);
  return roleLabel(picked);
};
